import router, { menuRoutesEnum } from '@/router'
import { getI18n } from '@/plugins/i18n'
import { useNotify } from '@/composables/notify'

import { useDefaultErrorHandler } from '@/composables/data-grid'

import { useRegistryStore } from '@/stores/registry.store'
import { useGarageStore } from '@/stores/garage.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useSubjectService } from '@/services/subject.service'

export const usePartialSave = () => {
  const { notifyError, notifySuccess } = useNotify()
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const garageStore = useGarageStore()
  const portalStore = usePortalStore()
  const webSiteStore = useWebSiteStore()
  const { isNameExtMandatory } = useSubjectService()

  const onRowValidating = async(e, gridRef, autoSave = true, extraData = {}) => {
    let customerSaved = true
    let vehicleSaved = true

    const columns = e.component.getVisibleColumns()
    const invalidFieldsName = e.brokenRules.map(rule => columns.find(col => col.caption === rule.validator._validationInfo.result.name)?.dataField).filter(Boolean)
    if (!e.isValid && e.brokenRules?.length > 0 && e.brokenRules.length === invalidFieldsName.length && invalidFieldsName.every(field => field === 'customer_id' || field === 'vehicle_id')) {
      e.component.beginCustomLoading()
      e.component.option('disabled', true)
      try {
        const { isSaveDisabled, partialSave, getCustomerForPartialSave, getVehicleForPartialSave } = usePartialSave()
        const registryStore = useRegistryStore()

        const disabledVehicleFields = [['plate_number', 'vin'], 'name', 'supplier_brand_ref']
        const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'name', 'mobile']
        const addressRequiredFields = ['country', ['state', 'state_text'], 'zip_code', ['city', 'city_text'], 'street']

        let currentCustomerId = null

        // Customer
        const isValidCustomer = !isSaveDisabled('customer', disabledOwnerFields, null, addressRequiredFields, e.component)
        if (isValidCustomer && !(e.oldData && (e.oldData?.customer?.id && e.newData?.customer?.id !== null))) {
          const currentCustomer = await getCustomerForPartialSave({ ...e?.oldData?.customer, ...e?.newData?.customer }, registryStore.api.getSubjectNextIdAsync)

          const res = await partialSave(currentCustomer, registryStore.api.createSubjectAsync, { grid: gridRef.value?.instance, target: menuRoutesEnum.SUBJECTS, queryFields: ['code'], entityName: 'common.customer'})
          if (res?.customer?.id) {
            currentCustomerId = res?.customer?.id
            const rowIndex = e.component.getRowIndexByKey(e.key)
            const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === res.customer.id)
            e.component.cellValue(rowIndex, 'customer_id', customer)
          } else {
            customerSaved = false
          }
        }

        // Vehicle
        const isValidVehicle = !isSaveDisabled('vehicle', disabledVehicleFields, null, [], e.component)
        if (isValidVehicle && !(e.oldData && (e.oldData?.vehicle?.id && e.newData?.vehicle?.id !== null))) {
          const customer_id = currentCustomerId || e?.newData?.customer?.id || e?.oldData?.customer?.id
          const currentVehicle = await getVehicleForPartialSave({ ...e?.oldData?.vehicle, ...e?.newData?.vehicle, ...extraData.value }, customer_id, registryStore.api.getVehicleNextIdAsync)

          const res = await partialSave(currentVehicle, registryStore.api.createVehicleAsync, { grid: gridRef.value?.instance, target: menuRoutesEnum.VEHICLES, queryFields: ['vin'], entityName: 'common.vehicle' })
          if (res) {
            const rowIndex = e.component.getRowIndexByKey(e.key)
            const vehicle = (await registryStore.getOrFetchVehiclesAsync()).find(c => c.id === res.id)
            e.component.cellValue(rowIndex, 'vehicle_id', vehicle)
          } else {
            vehicleSaved = false
          }
        }

        if (customerSaved && vehicleSaved && autoSave) {
          e.component.saveEditData()
        }
      } finally {
        e.component.option('disabled', false)
        e.component.endCustomLoading()
      }
    }

    return customerSaved && vehicleSaved
  }

  const mappingTargetErrorField = (details) => {
    const mapping = { vin: 'vehicle.vin', plate_number: 'vehicle.plate_number', name: 'customer.name', name_ext: 'customer.name_ext' }
    return details?.map(detail => ({ ...detail, target: mapping[detail.target] || detail.target }))
  }

  const partialSave = async(data, api, options) => {
    if (!data) return null

    try {
      const res = await api(data)
      if (res.data) {
        if (res.data?.is_partial && !!options?.queryFields && !!options?.target) {
          notifySuccess(options?.entityName ? `${i18n.t(options.entityName)}: ${i18n.t('common.success_create_partial')}` : i18n.t('common.success_create_partial'),
            {
              actions: [
              {
                size: 'sm', label: i18n.t('common.complete_action'), color: 'white', handler: async() => {
                  const args = {}
                  for (const field of options.queryFields) { args[field] = res.data[field] }
                  if (options.target === menuRoutesEnum.SUBJECTS) {
                    args.openEdit = true
                    args.subjectValidation = portalStore.subjectValidationTypesEnum.PARTIAL
                    args.tab = 0
                  }
                  const route = { path: options.target, query: args }
                  await router.push(route)
                }
              }
              ]
            })
          return res.data
        }
        notifySuccess(i18n.t('common.success_create'))
        return res.data
      }
      return null
    } catch (err) {
      data.id = null
      const error = {
        error: {
          httpStatus: err?.response?.status,
          errorDetails: { details: mappingTargetErrorField(err?.response?.data?.details) }
        },
        component: options?.grid
      }
      const isManaged = options?.grid ? useDefaultErrorHandler(error) : false
      if (!isManaged) { notifyError(i18n.t('error.create')) }
      return null
    }
  }

  const satisfiesRequiredRules = (obj, rules = []) => {
    return rules.every(rule => {
      if (typeof rule === 'string') {
        return !!obj?.[rule]
      }

      if (Array.isArray(rule)) {
        return rule.some(field => !!obj?.[field])
      }

      return false
    })
  }

  const isSaveDisabled = (key, requiredFields, gridRef, addressRequiredFields, gridInstance = null) => {
    const grid = gridRef?.value?.instance || gridInstance
    if (!grid) return true

    const editingRow = grid.getVisibleRows().find(row => row.isEditing)
    if (!editingRow) return true

    const obj = editingRow.data?.[key]
    if (!obj) return true

    if (obj.id) return true

    if (!satisfiesRequiredRules(obj, requiredFields)) return true

    if (key === 'customer' && isNameExtMandatory(obj.subject_type_ref) && !obj.name_ext) return true

    if (obj.address && addressRequiredFields.length > 0) {
      const hasSomeAddressValue = Object.values(obj.address).some(v => !!v)

      if (hasSomeAddressValue) {
        const addressValid = satisfiesRequiredRules(
          obj.address,
          addressRequiredFields
        )

        if (!addressValid) return true
      }
    }

    if (key === 'vehicle') {
      const currentGarageReason = garageStore.garageReasons.find(i => i.id === editingRow.data.garage_reason_ref)
      const interventionType = garageStore.interventionTypes.find(i => i.id === currentGarageReason?.intervention_type_ref)
      if (interventionType?.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE && (obj.km === null || obj.km === undefined)) return true
    }

    return false
  }

  const getCustomerForPartialSave = async(currentCustomer, nextIdResponseFunction) => {
    const nextIdResponse = await nextIdResponseFunction()
    currentCustomer.code = nextIdResponse.data
    currentCustomer.type = registryStore.subjectEntityTypes.CUSTOMER
    currentCustomer.customer ??= {}
    currentCustomer.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id

    if (!currentCustomer?.address?.street) {
      delete currentCustomer.address
    }

    delete currentCustomer.id
    return currentCustomer
  }

  const getVehicleForPartialSave = async(currentVehicle, customer_id, nextIdResponseFunction) => {
    const nextIdResponse = await nextIdResponseFunction()
    currentVehicle.id = nextIdResponse.data
    currentVehicle.status = registryStore.vehicleStatusTypesEnum.TRANSITED
    currentVehicle.customer_id = customer_id

    delete currentVehicle.id
    return currentVehicle
  }

  return { partialSave, isSaveDisabled, getCustomerForPartialSave, getVehicleForPartialSave, onRowValidating }
}
